import React, { useState, useEffect } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  QrCodeIcon, 
  Plus, 
  MessageSquare,
  MessageCircle,
  Settings, 
  LogOut,
  Download,
  Copy,
  Send,
  MapPin,
  Mail,
  Phone,
  Calendar,
  Crown,
  Search,
  Shield,
  Bell,
  Trash2,
  Edit2,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Get app domain from environment or use default
const APP_DOMAIN = process.env.REACT_APP_DOMAIN || window.location.hostname === 'localhost' ? 'localhost:3000' : window.location.host;

export default function Dashboard() {
  const { user, logout, loading } = useAuth();
  
  // Redirect if not authenticated - check BEFORE any other hooks
  if (!loading && !user) {
    return <Navigate to="/auth" replace />;
  }
  
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('qr-codes');
  const [qrCodes, setQrCodes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingQRCodes, setLoadingQRCodes] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent-message'); // Sorting state
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [selectedQRForNotifications, setSelectedQRForNotifications] = useState(null);
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [notificationPhones, setNotificationPhones] = useState([]);
  const [contactsLimit, setContactsLimit] = useState(1);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingQRCode, setEditingQRCode] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [selectedConversationQRCode, setSelectedConversationQRCode] = useState(null);

  // Handle logout with navigation
  const handleLogout = () => {
    logout();
    // Use window.location for clean redirect without React state conflicts
    window.location.href = '/';
  };

  useEffect(() => {
    if (user) {
      if (activeTab === 'qr-codes') {
        fetchQRCodes();
      } else if (activeTab === 'messages') {
        fetchMessages();
      }
    }
  }, [user, activeTab]);

  const fetchQRCodes = async () => {
    setLoadingQRCodes(true);
    try {
      const response = await api.getUserQRCodes();
      setQrCodes(response.data);
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
      toast.error('Failed to load QR codes');
    } finally {
      setLoadingQRCodes(false);
    }
  };

  const fetchMessages = async (qrCodeId = null) => {
    setLoadingMessages(true);
    try {
      const response = await api.getUserMessages(qrCodeId);
      setMessages(response.data);
      // Set the QR code for the conversation view
      if (qrCodeId && response.data.length > 0) {
        setSelectedConversationQRCode(response.data[0].qr_code);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCreateQRCode = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    
    const qrData = {
      title: formData.get('title'),
      greeting_message: formData.get('greeting_message') || null,
      auto_delete_days: formData.get('auto_delete_days') || null
    };

    try {
      const response = await api.generateQRCode(qrData);
      toast.success('QR code created successfully!');
      setShowCreateDialog(false);
      fetchQRCodes();
      event.target.reset();
    } catch (error) {
      console.error('Failed to create QR code:', error);
      toast.error(error.response?.data?.detail || 'Failed to create QR code');
    }
  };

  const handleReply = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const replyContent = formData.get('reply-content');

    try {
      await api.replyToMessage(selectedMessage.id, replyContent);
      toast.success('Reply sent successfully!');
      setShowReplyDialog(false);
      setSelectedMessage(null);
      fetchMessages();
      event.target.reset();
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleSendChatReply = async (messageId) => {
    if (!replyText.trim() || sendingReply) return;
    
    setSendingReply(true);
    try {
      await api.replyToMessage(messageId, replyText);
      toast.success('Reply sent!');
      setReplyText('');
      // Refresh messages to show the new reply
      if (selectedConversationQRCode) {
        await fetchMessages(selectedConversationQRCode.id);
      } else {
        await fetchMessages();
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadQRCode = (qrCode) => {
    const canvas = document.createElement('canvas');
    const svg = document.querySelector(`#qr-${qrCode.id} svg`);
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.onload = function() {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `${qrCode.title}-qr-code.png`;
      link.href = canvas.toDataURL();
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    // Show relative time for recent messages (less than 24 hours)
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    
    // For older messages, show full date/time in user's local timezone
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getSubscriptionBadgeColor = (plan) => {
    switch (plan) {
      case 'premium': return 'bg-purple-600';
      case 'basic': return 'bg-blue-600';
      default: return 'bg-gray-600';
    }
  };

  // Enhanced search across all attributes
  const filteredQRCodes = qrCodes.filter(qr => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    
    // Search in QR code attributes
    const matchesTitle = qr.title?.toLowerCase().includes(search);
    const matchesUID = qr.uid?.toLowerCase().includes(search);
    const matchesCreatedDate = qr.created_at && formatDate(qr.created_at).toLowerCase().includes(search);
    const matchesLastMessage = qr.last_message_at && formatDate(qr.last_message_at).toLowerCase().includes(search);
    
    return matchesTitle || matchesUID || matchesCreatedDate || matchesLastMessage;
  });

  // Sort the filtered results
  const sortedQRCodes = [...filteredQRCodes].sort((a, b) => {
    switch (sortBy) {
      case 'recent-message':
        // Most recent message first, fallback to created date
        const aTime = a.last_message_at ? new Date(a.last_message_at).getTime() : new Date(a.created_at).getTime();
        const bTime = b.last_message_at ? new Date(b.last_message_at).getTime() : new Date(b.created_at).getTime();
        return bTime - aTime;
      
      case 'newest-created':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      
      case 'oldest-created':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      
      case 'most-messages':
        return (b.message_count || 0) - (a.message_count || 0);
      
      case 'alphabetical':
        return (a.title || '').localeCompare(b.title || '');
      
      default:
        return 0;
    }
  });

  const filteredMessages = messages.filter(msg => 
    msg.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (msg.qr_code && msg.qr_code.title.toLowerCase().includes(searchTerm.toLowerCase()))
  ).reverse(); // Reverse order so newest messages appear at bottom

  // Ref for chat container to enable auto-scroll
  const chatContainerRef = React.useRef(null);

  // Auto-scroll to bottom when messages change
  React.useEffect(() => {
    if (chatContainerRef.current && activeTab === 'messages') {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [filteredMessages, activeTab]);

  const handleManageNotifications = async (qrCode) => {
    setSelectedQRForNotifications(qrCode);
    try {
      const response = await api.getNotificationContacts(qrCode.uid);
      setNotificationEmails(response.data.notification_emails || []);
      setNotificationPhones(response.data.notification_phones || []);
      setContactsLimit(response.data.contacts_limit || 1);
      setShowNotificationDialog(true);
    } catch (error) {
      toast.error('Failed to load notification contacts');
    }
  };

  const addNotificationEmail = () => {
    const totalContacts = notificationEmails.length + notificationPhones.length;
    if (totalContacts >= contactsLimit) {
      toast.error(`Your ${user.subscription_plan} plan allows ${contactsLimit} notification contacts per QR code`);
      return;
    }
    setNotificationEmails([...notificationEmails, '']);
  };

  const addNotificationPhone = () => {
    const totalContacts = notificationEmails.length + notificationPhones.length;
    if (totalContacts >= contactsLimit) {
      toast.error(`Your ${user.subscription_plan} plan allows ${contactsLimit} notification contacts per QR code`);
      return;
    }
    setNotificationPhones([...notificationPhones, '']);
  };

  const removeNotificationEmail = (index) => {
    setNotificationEmails(notificationEmails.filter((_, i) => i !== index));
  };

  const removeNotificationPhone = (index) => {
    setNotificationPhones(notificationPhones.filter((_, i) => i !== index));
  };

  const updateNotificationEmail = (index, value) => {
    const updated = [...notificationEmails];
    updated[index] = value;
    setNotificationEmails(updated);
  };

  const updateNotificationPhone = (index, value) => {
    const updated = [...notificationPhones];
    updated[index] = value;
    setNotificationPhones(updated);
  };

  const saveNotificationContacts = async () => {
    try {
      // Filter out empty values
      const validEmails = notificationEmails.filter(email => email.trim() !== '');
      const validPhones = notificationPhones.filter(phone => phone.trim() !== '');

      await api.updateNotificationContacts(selectedQRForNotifications.uid, {
        uid: selectedQRForNotifications.uid,
        notification_emails: validEmails,
        notification_phones: validPhones
      });
      
      toast.success('Notification contacts updated successfully!');
      setShowNotificationDialog(false);
      fetchQRCodes(); // Refresh QR codes to see updates
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update notification contacts');
    }
  };


  const handleEditQRCode = (qrCode) => {
    setEditingQRCode(qrCode);
    setShowEditDialog(true);
  };

  const handleSaveQRCodeEdit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    
    const qrData = {
      title: formData.get('title'),
      greeting_message: formData.get('greeting_message'),
      auto_delete_days: formData.get('auto_delete_days') || null,
      allow_reply_contact: formData.get('allow_reply_contact') === 'on'
    };

    try {
      await api.updateQRCode(editingQRCode.uid, qrData);
      toast.success('QR code updated successfully!');
      setShowEditDialog(false);
      setEditingQRCode(null);
      fetchQRCodes();
    } catch (error) {
      console.error('Failed to update QR code:', error);
      toast.error(error.response?.data?.detail || 'Failed to update QR code');
    }
  };

  const handleDeleteQRCode = async (qrCode) => {
    if (!confirm(`Are you sure you want to delete "${qrCode.title}"? This will also delete all associated messages. This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await api.deleteQRCode(qrCode.uid);
      toast.success(`QR code deleted successfully. ${response.data.deleted_messages} message(s) also deleted.`);
      fetchQRCodes();
    } catch (error) {
      console.error('Failed to delete QR code:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete QR code');
    }
  };


  // Product management functions
  const fetchProducts = async () => {
    try {
      const response = await api.getAdminProducts();
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      qr_codes_included: product.qr_codes_included.toString(),
      stock_quantity: product.stock_quantity ? product.stock_quantity.toString() : '',
      is_active: product.is_active
    });
    setShowProductDialog(true);
  };

  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({
      name: '',
      description: '',
      price: '',
      category: 'stickers',
      qr_codes_included: '8',
      stock_quantity: '',
      is_active: true
    });
    setShowProductDialog(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...productForm,
        price: parseFloat(productForm.price),
        qr_codes_included: parseInt(productForm.qr_codes_included),
        stock_quantity: productForm.stock_quantity ? parseInt(productForm.stock_quantity) : null
      };

      if (editingProduct) {
        await api.updateProduct(editingProduct.id, productData);
        toast.success('Product updated successfully!');
      } else {
        await api.createProduct(productData);
        toast.success('Product created successfully!');
      }

      setShowProductDialog(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Failed to save product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await api.deleteProduct(productId);
      toast.success('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-2">
                <QrCodeIcon className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-bold text-gray-900">iComment.app</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className={`${getSubscriptionBadgeColor(user?.subscription_plan)} text-white`} data-testid="subscription-badge">
                {user?.subscription_plan === 'premium' && <Crown className="h-3 w-3 mr-1" />}
                {user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1)}
              </Badge>
              {user?.role === 'admin' && (
                <Link to="/admin">
                  <Badge className="bg-red-600 text-white hover:bg-red-700" data-testid="admin-link">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin Panel
                  </Badge>
                </Link>
              )}
              <span className="text-sm text-gray-600" data-testid="user-email">
                {user?.email || user?.username}
              </span>
              <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Usage Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="usage-stats">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">QR Codes</CardTitle>
              <CardDescription>
                {user?.qr_codes_used || 0} / {user?.usage_limits?.qr_codes_limit === -1 ? '∞' : user?.usage_limits?.qr_codes_limit || 0}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ 
                    width: user?.usage_limits?.qr_codes_limit === -1 ? '100%' : 
                           `${Math.min(100, (user?.qr_codes_used / user?.usage_limits?.qr_codes_limit) * 100)}%` 
                  }}
                ></div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Messages</CardTitle>
              <CardDescription>
                {user?.messages_used || 0} / {user?.usage_limits?.messages_limit === -1 ? '∞' : user?.usage_limits?.messages_limit || 0}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ 
                    width: user?.usage_limits?.messages_limit === -1 ? '100%' : 
                           `${Math.min(100, (user?.messages_used / user?.usage_limits?.messages_limit) * 100)}%` 
                  }}
                ></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Subscription</CardTitle>
              <CardDescription>
                {user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1)} Plan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/subscription">
                <Button size="sm" variant="outline" data-testid="upgrade-btn">
                  Change or Cancel Plan
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search QR codes and messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="dashboard-tabs">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="qr-codes" data-testid="qr-codes-tab">
              <QrCodeIcon className="h-4 w-4 mr-2" />
              QR Codes ({qrCodes.length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages ({messages.length})
            </TabsTrigger>
          </TabsList>

          {/* QR Codes Tab */}
          <TabsContent value="qr-codes" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900" data-testid="qr-codes-title">
                Your QR Codes
              </h2>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-qr-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create QR Code
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New QR Code</DialogTitle>
                    <DialogDescription>
                      Create a new QR code for your item with a custom title and greeting.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateQRCode} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Item Title *</Label>
                      <Input
                        id="title"
                        name="title"
                        placeholder="e.g., My Wallet, Laptop Bag"
                        required
                        data-testid="create-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="greeting_message">Greeting Message</Label>
                      <Textarea
                        id="greeting_message"
                        name="greeting_message"
                        placeholder="Message that finders will see when they scan your QR code"
                        rows={3}
                        data-testid="create-greeting-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="auto_delete_days">Auto-delete Messages After (Days)</Label>
                      <Input
                        id="auto_delete_days"
                        name="auto_delete_days"
                        type="number"
                        placeholder="Optional: Auto-delete messages after X days"
                        data-testid="create-auto-delete-input"
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setShowCreateDialog(false)}
                        data-testid="create-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" data-testid="create-submit-btn">
                        Create QR Code
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>


            {/* Search and Sort Bar */}
            {qrCodes.length > 0 && (
              <div className="flex gap-4 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search by title, code, date, or messages..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="qr-search-input"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[220px]" data-testid="sort-dropdown">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent-message">Most Recent Message</SelectItem>
                    <SelectItem value="newest-created">Newest Created</SelectItem>
                    <SelectItem value="oldest-created">Oldest Created</SelectItem>
                    <SelectItem value="most-messages">Most Messages</SelectItem>
                    <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}


            {loadingQRCodes ? (
              <div className="text-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p>Loading QR codes...</p>
              </div>
            ) : sortedQRCodes.length === 0 ? (
              <Card className="text-center py-8" data-testid="no-qr-codes">
                <CardContent>
                  <QrCodeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    {qrCodes.length === 0 ? "You haven't created any QR codes yet." : "No QR codes match your search."}
                  </p>
                  {qrCodes.length === 0 && (
                    <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-qr-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First QR Code
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sortedQRCodes.map((qrCode) => (
                  <Card key={qrCode.id} className="hover:shadow-md transition-shadow" data-testid={`qr-card-${qrCode.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        {/* Left section - Title and info */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900" data-testid={`qr-title-${qrCode.id}`}>
                                {qrCode.title}
                              </h3>
                              <div className="flex items-center space-x-4 mt-1">
                                <span className="text-sm text-gray-500" data-testid={`qr-uid-${qrCode.id}`}>
                                  Code: {qrCode.uid}
                                </span>
                                <Badge variant="outline" className="text-xs" data-testid={`qr-messages-${qrCode.id}`}>
                                  {qrCode.message_count} {qrCode.message_count === 1 ? 'message' : 'messages'}
                                </Badge>
                                {qrCode.last_message_at && (
                                  <span className="text-xs text-gray-500 flex items-center">
                                    <MessageSquare className="h-3 w-3 mr-1" />
                                    Last message: {formatDate(qrCode.last_message_at)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right section - Action buttons */}
                        <div className="flex items-center space-x-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline"
                                data-testid={`view-qr-${qrCode.id}`}
                              >
                                <QrCodeIcon className="h-4 w-4 mr-1" />
                                View QR
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>{qrCode.title}</DialogTitle>
                                <DialogDescription>
                                  Code: {qrCode.uid}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="flex justify-center p-6 bg-gray-50 rounded-lg" id={`qr-${qrCode.id}`}>
                                  <QRCode 
                                    value={`https://${APP_DOMAIN}/${qrCode.uid}`}
                                    size={200}
                                    data-testid={`qr-image-${qrCode.id}`}
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <Button 
                                    variant="outline" 
                                    onClick={() => copyToClipboard(`https://${APP_DOMAIN}/${qrCode.uid}`)}
                                    className="flex-1"
                                  >
                                    <Copy className="h-4 w-4 mr-2" />
                                    Copy Link
                                  </Button>
                                  <Button 
                                    variant="outline"
                                    onClick={() => downloadQRCode(qrCode)}
                                    className="flex-1"
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>

                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditQRCode(qrCode)}
                            data-testid={`edit-qr-${qrCode.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>

                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleManageNotifications(qrCode)}
                            title="Manage Notifications"
                            data-testid={`manage-notifications-${qrCode.id}`}
                          >
                            <Bell className="h-4 w-4" />
                          </Button>

                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDeleteQRCode(qrCode)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-qr-${qrCode.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>

                          {qrCode.message_count > 0 && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setActiveTab('messages');
                                fetchMessages(qrCode.id);
                              }}
                              data-testid={`view-messages-${qrCode.id}`}
                            >
                              View Messages
                              <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab - Chat Interface */}
          <TabsContent value="messages" className="space-y-0">
            {loadingMessages ? (
              <div className="text-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p>Loading messages...</p>
              </div>
            ) : filteredMessages.length === 0 ? (
              <Card className="text-center py-8" data-testid="no-messages">
                <CardContent>
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    {messages.length === 0 ? "No messages yet. Messages will appear here when someone scans your QR codes." : "No messages match your search."}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-[600px] flex flex-col">
                {/* Chat Header */}
                <div className="border-b bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900" data-testid="messages-title">
                        {selectedConversationQRCode ? (
                          <>
                            <span className="text-blue-600">{selectedConversationQRCode.title}</span>
                            <span className="text-sm text-gray-500 ml-2">({selectedConversationQRCode.uid})</span>
                          </>
                        ) : (
                          'All Messages'
                        )}
                      </h2>
                      <p className="text-sm text-gray-500">
                        {filteredMessages.length} {filteredMessages.length === 1 ? 'message' : 'messages'}
                      </p>
                    </div>
                    {selectedConversationQRCode && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedConversationQRCode(null);
                          fetchMessages();
                        }}
                      >
                        View All Messages
                      </Button>
                    )}
                  </div>
                </div>

                {/* Chat Messages Area */}
                <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50">
                  {filteredMessages.map((message, index) => {
                    const isFromFinder = message.sender_type === 'finder';
                    const isFirstInThread = index === 0 || filteredMessages[index - 1].qr_code?.id !== message.qr_code?.id;
                    
                    return (
                      <div key={message.id}>
                        {/* QR Code Divider (when switching between different QR codes) */}
                        {!selectedConversationQRCode && isFirstInThread && (
                          <div className="flex items-center justify-center my-4">
                            <div className="flex-1 border-t border-gray-300"></div>
                            <Badge 
                              variant="outline" 
                              className="mx-4 px-3 py-1 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                              onClick={() => {
                                if (message.qr_code && message.qr_code.id) {
                                  setSelectedConversationQRCode(message.qr_code);
                                  fetchMessages(message.qr_code.id);
                                }
                              }}
                              title="Click to open conversation and reply"
                            >
                              {message.qr_code?.title || 'Unknown Item'} ({message.qr_code?.uid}) 💬
                            </Badge>
                            <div className="flex-1 border-t border-gray-300"></div>
                          </div>
                        )}

                        {/* Message Bubble */}
                        <div className={`flex ${isFromFinder ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[70%] ${isFromFinder ? '' : 'flex flex-col items-end'}`}>
                            {/* Sender Badge */}
                            <Badge 
                              className={`mb-1 ${
                                isFromFinder 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {isFromFinder ? '📨 From Finder' : '✅ Your Reply'}
                            </Badge>
                            
                            {/* Message Content */}
                            <div 
                              className={`rounded-2xl px-4 py-3 shadow-sm ${
                                isFromFinder 
                                  ? 'bg-white border border-gray-200' 
                                  : 'bg-blue-600 text-white'
                              }`}
                              data-testid={`message-content-${message.id}`}
                            >
                              <p className={`text-sm leading-relaxed ${isFromFinder ? 'text-gray-900' : 'text-white'}`}>
                                {message.content}
                              </p>
                            </div>

                            {/* Message Meta Info */}
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span>{formatDate(message.created_at)}</span>
                              {isFromFinder && (message.sender_email || message.sender_phone) && (
                                <>
                                  <div className="flex items-center text-green-600">
                                    <MessageCircle className="h-3 w-3 mr-1" />
                                    <span className="font-medium">Reply-enabled</span>
                                  </div>
                                  {!selectedConversationQRCode && message.qr_code && message.qr_code.id && (
                                    <button
                                      onClick={() => {
                                        setSelectedConversationQRCode(message.qr_code);
                                        fetchMessages(message.qr_code.id);
                                      }}
                                      className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                                      data-testid={`reply-btn-${message.id}`}
                                    >
                                      <Send className="h-3 w-3 mr-1" />
                                      Reply
                                    </button>
                                  )}
                                </>
                              )}
                              {isFromFinder && message.location_data && (
                                <button
                                  onClick={() => {
                                    const { latitude, longitude } = message.location_data;
                                    window.open(`https://maps.google.com/maps?q=${latitude},${longitude}`, '_blank');
                                  }}
                                  className="flex items-center text-blue-600 hover:text-blue-800"
                                  data-testid={`view-location-${message.id}`}
                                >
                                  <MapPin className="h-3 w-3 mr-1" />
                                  View Location
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Reply Input Area - Only show if viewing specific conversation */}
                {selectedConversationQRCode && filteredMessages.some(m => m.sender_type === 'finder') && (
                  <div className="border-t bg-white px-6 py-4">
                    <div className="flex items-end space-x-3">
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            const finderMessage = filteredMessages.find(m => m.sender_type === 'finder');
                            if (finderMessage) {
                              handleSendChatReply(finderMessage.id);
                            }
                          }
                        }}
                        data-testid="chat-reply-input"
                      />
                      <Button
                        onClick={() => {
                          const finderMessage = filteredMessages.find(m => m.sender_type === 'finder');
                          if (finderMessage) {
                            handleSendChatReply(finderMessage.id);
                          }
                        }}
                        disabled={!replyText.trim() || sendingReply}
                        className="h-[60px] px-6"
                        data-testid="chat-send-btn"
                      >
                        {sendingReply ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Send
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Press Enter to send, Shift+Enter for new line</p>
                  </div>
                )}
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Notification Contacts Dialog */}
        <Dialog open={showNotificationDialog} onOpenChange={setShowNotificationDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Manage Notification Contacts</DialogTitle>
              <DialogDescription>
                Configure email addresses and phone numbers that will receive notifications when someone messages this QR code.
                <br />
                <span className="text-sm font-medium text-blue-600">
                  Your {user?.subscription_plan || 'free'} plan allows {contactsLimit} notification contacts per QR code.
                </span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Email Notifications */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-base font-medium">Email Notifications</Label>
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    onClick={addNotificationEmail}
                    disabled={(notificationEmails.length + notificationPhones.length) >= contactsLimit}
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    Add Email
                  </Button>
                </div>
                <div className="space-y-2">
                  {notificationEmails.map((email, index) => (
                    <div key={index} className="flex space-x-2">
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => updateNotificationEmail(index, e.target.value)}
                        placeholder="Enter email address"
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => removeNotificationEmail(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {notificationEmails.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">No email notifications configured</p>
                  )}
                </div>
              </div>

              {/* Phone Notifications */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-base font-medium">SMS Notifications</Label>
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    onClick={addNotificationPhone}
                    disabled={(notificationEmails.length + notificationPhones.length) >= contactsLimit}
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    Add Phone
                  </Button>
                </div>
                <div className="space-y-2">
                  {notificationPhones.map((phone, index) => (
                    <div key={index} className="flex space-x-2">
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => updateNotificationPhone(index, e.target.value)}
                        placeholder="Enter phone number (e.g., +1234567890)"
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => removeNotificationPhone(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {notificationPhones.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">No SMS notifications configured</p>
                  )}
                </div>
              </div>

              {/* Usage Info */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  <strong>Current usage:</strong> {notificationEmails.length + notificationPhones.length} / {contactsLimit} contacts
                  {contactsLimit === 1 && (
                    <span className="block mt-1">Upgrade to Basic for 2 emails + 2 phones per QR code, or Premium for 5 emails + 5 phones per QR code.</span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowNotificationDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={saveNotificationContacts}>
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>


        {/* Edit QR Code Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit QR Code</DialogTitle>
              <DialogDescription>
                Update the title and greeting message for your QR code.
              </DialogDescription>
            </DialogHeader>
            {editingQRCode && (
              <form onSubmit={handleSaveQRCodeEdit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Item Title *</Label>
                  <Input
                    id="edit-title"
                    name="title"
                    defaultValue={editingQRCode.title}
                    placeholder="e.g., My Wallet, Laptop Bag"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-greeting">Greeting Message</Label>
                  <Textarea
                    id="edit-greeting"
                    name="greeting_message"
                    defaultValue={editingQRCode.greeting_message}
                    placeholder="Message that finders will see when they scan your QR code"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-auto-delete">Auto-delete Messages After (Days)</Label>
                  <Input
                    id="edit-auto-delete"
                    name="auto_delete_days"
                    type="number"
                    defaultValue={editingQRCode.auto_delete_days || ''}
                    placeholder="Optional: Auto-delete messages after X days"
                  />
                </div>
                <div className="flex items-center justify-between p-5 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border-2 border-green-200">
                  <div className="space-y-1 flex-1 pr-4">
                    <Label htmlFor="allow-reply-contact" className="text-sm font-semibold text-gray-900 cursor-pointer flex items-center">
                      <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                      Allow Finders to Provide Reply Contact
                    </Label>
                    <p className="text-xs text-gray-700 leading-relaxed pl-6">
                      <span className="font-medium text-green-700">✓ Enabled:</span> Finders can share their email/phone for replies via iComment.app<br/>
                      <span className="font-medium text-red-700">✗ Disabled:</span> Finders cannot provide contact information
                    </p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Switch
                      id="allow-reply-contact"
                      name="allow_reply_contact"
                      defaultChecked={editingQRCode.allow_reply_contact !== false}
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-red-400"
                    />
                    <span className="text-xs font-semibold text-gray-600">
                      {editingQRCode.allow_reply_contact !== false ? '✓ ON' : '✗ OFF'}
                    </span>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowEditDialog(false);
                      setEditingQRCode(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Reply Dialog */}
        <Dialog open={showReplyDialog} onOpenChange={setShowReplyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reply to Message</DialogTitle>
              <DialogDescription>
                Send a reply to this message. The sender will receive your response.
              </DialogDescription>
            </DialogHeader>
            {selectedMessage && (
              <div className="bg-gray-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-gray-600">Original message:</p>
                <p className="text-gray-900">{selectedMessage.content}</p>
              </div>
            )}
            <form onSubmit={handleReply} className="space-y-4">
              <div>
                <Label htmlFor="reply-content">Your Reply</Label>
                <Textarea
                  id="reply-content"
                  name="reply-content"
                  placeholder="Type your reply here..."
                  required
                  rows={4}
                  data-testid="reply-textarea"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowReplyDialog(false)}
                  data-testid="reply-cancel-btn"
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="reply-send-btn">
                  Send Reply
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}